import { Loader } from '@brandandcelebrities/kolkit';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  OutlinedInput,
  Radio,
  RadioGroup,
} from '@material-ui/core';
import cn from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';

import ArrowRightAltIcon from '@material-ui/icons/ArrowRightAlt';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CloseIcon from '@material-ui/icons/Close';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';

import Markdown from 'components/atoms/Markdown';
import Typography from 'components/atoms/Typography';
import Avatar from 'components/molecules/Avatar';

import api from 'api';
import { conf } from 'config/env';
import { obfuscateEmail } from 'utils/obfuscate';

import { getKreators } from 'api/kreators';
import styles from './LinkInstagramButton.module.scss';

const LOGIN_SCOPE = `
  catalog_management,
  pages_show_list,
  business_management,
  instagram_basic,
  instagram_manage_insights,
  pages_read_engagement,
  email
`;

const USER_AND_FB_PAGES_SCOPES = `/me?fields=
  id,
  email,
  accounts {
    name,
    username,
    fan_count,
    category,
    picture {
      url
    },
    instagram_business_account {
      id,
      name,
      username,
      followers_count,
      profile_picture_url,
      ig_id
    }
  }
`;

type STEP = 'warning' | 'selectInstagram' | 'accountExists' | 'newAccount' | 'missingEmail' | 'failed';

interface Props {
  from: 'woomio' | 'kol-landing-page';
  showDownload?: boolean;
  showTermsAndConditionsInsideModal?: boolean;
  companyName?: string;
}

const langueNavigateur = navigator.language || navigator.userLanguage;

const LinkInstagramButton: React.FC<Props> = ({
  from = 'kol-landing-page',
  showDownload,
  showTermsAndConditionsInsideModal,
  companyName,
}) => {
  const intl = useIntl();

  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<STEP>('warning');

  const [instaCoData, setInstaCoData] = useState({
    email: null,
    instagramBusinessAccounts: [],
    selected: null,
  });

  const locale = langueNavigateur?.split('-')[0] !== 'fr' ? 'en' : 'fr';

  const handleCheck = useCallback((e) => setChecked(e.target.checked), []);

  const handleOpen = useCallback(() => setOpen(true), []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setLoading(false);
    setChecked(false);
    setStep('warning');
    setInstaCoData({
      email: null,
      instagramBusinessAccounts: [],
      selected: null,
    });
  }, []);

  const handleEmailChange = useCallback((value) => {
    setInstaCoData((d) => ({
      ...d,
      email: value,
    }));
  }, []);

  const handlePagesFail = useCallback(() => {
    setLoading(false);
    setStep('failed');
  }, []);

  const handleSubmit = useCallback(
    async ({ email, instagram, facebook }) => {
      try {
        const response = await api.registerByInstaCo({ email, instagram, facebook });

        if (response?.data?.confirmation_sent) {
          setStep('newAccount');
        } else {
          setStep('accountExists');
        }
      } catch (error) {
        handlePagesFail();
        return false;
      }
    },
    [handlePagesFail],
  );

  const handleSubmitWithCustomEmail = useCallback(() => {
    if (instaCoData?.instagramBusinessAccounts?.length === 1) {
      handleSubmit({
        email: instaCoData.email,
        instagram: instaCoData?.instagramBusinessAccounts[0],
        facebook: { access_token: window.FB.getAccessToken() },
      });
    } else {
      setStep('selectInstagram');
    }
  }, [instaCoData, handleSubmit]);

  const handleSubmitWithSelectedInstagram = useCallback(() => {
    if (instaCoData.selected) {
      handleSubmit({
        email: instaCoData.email,
        instagram: instaCoData?.instagramBusinessAccounts.find((instagram) => instagram?.uid === instaCoData.selected),
        facebook: { access_token: window.FB.getAccessToken() },
      });
    } else {
      handlePagesFail();
    }
  }, [instaCoData.selected, instaCoData.email, instaCoData?.instagramBusinessAccounts, handleSubmit, handlePagesFail]);

  const getAllPagesCallback = useCallback(
    (response) => {
      if (response?.status === 'connected' && response?.authResponse?.accessToken) {
        window.FB?.api?.(USER_AND_FB_PAGES_SCOPES, async (fbData) => {
          const instagramBusinessAccounts = fbData?.accounts?.data
            ?.filter((fbPage) => fbPage?.instagram_business_account?.id) // Only FB page has instagram business account
            ?.map((fbPage) => ({
              business_id: fbPage?.instagram_business_account?.id,
              uid: String(fbPage?.instagram_business_account?.ig_id),
              name: fbPage?.instagram_business_account?.name || fbPage?.name,
              username: fbPage?.instagram_business_account?.username,
              profile_picture_url:
                fbPage?.instagram_business_account?.profile_picture_url || fbPage?.picture?.data?.url,
            }));

          if (instagramBusinessAccounts?.length > 0) {
            setInstaCoData({
              email: fbData?.email,
              instagramBusinessAccounts,
              selected: instagramBusinessAccounts[0]?.uid,
            });

            if (!fbData?.email || fbData?.email === '') {
              setStep('missingEmail');
            }

            if (fbData?.email) {
              if (instagramBusinessAccounts?.length === 1) {
                await handleSubmit({
                  email: fbData?.email,
                  instagram: instagramBusinessAccounts[0],
                  facebook: { access_token: window.FB.getAccessToken() },
                });
              } else {
                setStep('selectInstagram');
              }
            }
          } else {
            handlePagesFail();
          }
        });
      } else {
        handlePagesFail();
      }

      setLoading(false);
    },
    [handlePagesFail, handleSubmit],
  );

  const getAllPages = useCallback(() => {
    setLoading(true);
    window.FB?.login(getAllPagesCallback, {
      scope: LOGIN_SCOPE,
    });
  }, [getAllPagesCallback]);

  const getFacebookKreators = useCallback(async () => {
    setLoading(true);
    const response = await getKreators(companyName);

    const { auth_url } = response;

    // Move to the new page
    window.location.href = auth_url;
  }, [companyName]);

  const handleRadioChange = useCallback((event) => {
    setInstaCoData((d) => ({
      ...d,
      selected: event?.target?.value,
    }));
  }, []);

  useEffect(() => {
    if (window.FB) {
      window.FB.init({
        appId: conf.fbc.clientId,
        cookie: true,
        xfbml: true,
        version: 'v23.0',
      });
    }
  }, []);

  const renderContent = useMemo(() => {
    if (step === 'warning')
      return (
        <>
          <DialogContent className={styles.dialogContent}>
            <div className={styles.modalContent}>
              <div className={styles.textSection}>
                <h2 className={styles.title}>
                  <FormattedMessage id="linkInstagram.modal.warning.title" />
                </h2>
                <p className={styles.subtitle}>
                  <FormattedMessage id="linkInstagram.modal.warning.subTitle" />
                </p>
                <div className={styles.message}>
                  <Markdown>{intl.formatMessage({ id: 'linkInstagram.modal.warning.message' })}</Markdown>
                </div>
              </div>

              {showTermsAndConditionsInsideModal && (
                <div className={styles.termsConditions}>
                  <div className={styles.customCheckbox}>
                    <input type="checkbox" id="terms-checkbox" checked={checked} onChange={handleCheck} />
                  </div>
                  <p className={styles.termsText}>
                    <Markdown>{intl.formatMessage({ id: 'linkInstagram.termsConditions' })}</Markdown>
                  </p>
                </div>
              )}

              <button
                className={styles.linkButton}
                type="button"
                onClick={getFacebookKreators}
                disabled={showTermsAndConditionsInsideModal && !checked}
              >
                <FormattedMessage id="linkInstagram.button" />
              </button>
            </div>
          </DialogContent>
        </>
      );

    if (step === 'newAccount')
      return (
        <>
          <DialogContent className={styles.dialogContent}>
            <div className="flex fdc gap22 aic tac">
              <div className={styles.headIcon}>
                <CheckCircleIcon />
              </div>
              <Typography variant="title/modal-title">
                <FormattedMessage
                  id={
                    from === 'kol-landing-page'
                      ? 'KolLandingPage.modal.success.newAccount.title'
                      : 'linkInstagram.modal.success.newAccount.title'
                  }
                />
              </Typography>
              <Typography variant="text/body small" className={styles.body}>
                <Markdown>
                  {intl.formatMessage(
                    {
                      id:
                        from === 'kol-landing-page'
                          ? 'KolLandingPage.modal.success.newAccount.message'
                          : 'linkInstagram.modal.success.newAccount.message',
                    },
                    { email: obfuscateEmail(instaCoData.email || '') },
                  )}
                </Markdown>
              </Typography>
            </div>
          </DialogContent>
          <DialogActions classes={{ root: styles.dialogActions }}>
            <Button classes={{ root: styles.button, endIcon: styles.endIcon }} onClick={handleClose}>
              <FormattedMessage id="global.cta.close" />
            </Button>
          </DialogActions>
        </>
      );

    if (step === 'accountExists')
      return (
        <>
          <DialogContent className={styles.dialogContent}>
            <div className="flex fdc gap22 aic tac">
              <div className={styles.headIcon}>
                <CheckCircleIcon />
              </div>
              <Typography variant="title/modal-title">
                <FormattedMessage
                  id={
                    from === 'kol-landing-page'
                      ? 'KolLandingPage.modal.success.accountExists.title'
                      : 'linkInstagram.modal.success.accountExists.title'
                  }
                />
              </Typography>
              <Typography variant="text/body small" className={styles.body}>
                <Markdown>
                  {intl.formatMessage({
                    id:
                      from === 'kol-landing-page'
                        ? 'KolLandingPage.modal.success.accountExists.message'
                        : 'linkInstagram.modal.success.accountExists.message',
                  })}
                </Markdown>
              </Typography>
            </div>
          </DialogContent>
          <DialogActions classes={{ root: styles.dialogActions }}>
            {from === 'kol-landing-page' && (
              <a href="/login" className="mb20">
                <Button classes={{ root: styles.button, endIcon: styles.endIcon }}>
                  <FormattedMessage id="KolLandingPage.modal.success.accountExists.cta" />
                </Button>
              </a>
            )}
            {from === 'woomio' && (
              <Button classes={{ root: styles.button, endIcon: styles.endIcon }} onClick={handleClose}>
                <FormattedMessage id="global.cta.close" />
              </Button>
            )}
          </DialogActions>
        </>
      );

    if (step === 'missingEmail')
      return (
        <>
          <DialogContent className={styles.dialogContent}>
            <div className="flex fdc gap22">
              <Typography variant="title/modal-title">
                <FormattedMessage id="linkInstagram.modal.success.missingEmail.title" />
              </Typography>
              <Typography variant="text/body small" className={styles.body}>
                <Markdown>{intl.formatMessage({ id: 'linkInstagram.modal.success.missingEmail.message' })}</Markdown>
              </Typography>
              <OutlinedInput
                placeholder={intl.formatMessage({ id: 'linkInstagram.modal.success.missingEmail.placeholder' })}
                value={instaCoData.email}
                onChange={(event) => handleEmailChange(event?.target?.value)}
                classes={{
                  root: styles.textField,
                  focused: styles.focused,
                  notchedOutline: styles.notchedOutline,
                }}
              />
            </div>
          </DialogContent>
          <DialogActions classes={{ root: styles.dialogActions }}>
            <Button
              classes={{ root: styles.button }}
              onClick={handleSubmitWithCustomEmail}
              disabled={instaCoData?.email?.trim() === ''}
            >
              <FormattedMessage id="global.cta.valide" />
            </Button>
          </DialogActions>
        </>
      );

    if (step === 'selectInstagram')
      return (
        <>
          <DialogContent className={styles.dialogContent}>
            <div className="flex fdc gap22 aic tac">
              <div className={styles.headIcon}>
                <CheckCircleIcon />
              </div>
              <Typography variant="title/modal-title">
                <FormattedMessage id="linkInstagram.modal.multipleInstagram.title" />
              </Typography>
              <Typography variant="text/body small" className={styles.body}>
                <Markdown>{intl.formatMessage({ id: 'linkInstagram.modal.multipleInstagram.message' })}</Markdown>
              </Typography>
              <FormControl>
                <RadioGroup value={instaCoData.selected} onChange={handleRadioChange}>
                  {instaCoData.instagramBusinessAccounts.map((instagram) => (
                    <FormControlLabel
                      key={instagram?.uid}
                      value={instagram?.uid}
                      control={<Radio classes={{ root: styles.radio, checked: styles.checked }} />}
                      label={
                        <Avatar
                          name={`${instagram?.name} - ${instagram?.username}`}
                          avatar={instagram?.profile_picture_url}
                        />
                      }
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </div>
          </DialogContent>
          <DialogActions classes={{ root: styles.dialogActions }}>
            <Button
              classes={{ root: styles.button }}
              onClick={handleSubmitWithSelectedInstagram}
              disabled={!instaCoData.selected}
            >
              <FormattedMessage id="global.cta.valide" />
            </Button>
          </DialogActions>
        </>
      );

    if (step === 'failed')
      return (
        <>
          <DialogContent className={styles.dialogContent}>
            <div className="flex fdc gap22 aic tac">
              <div className={cn(styles.headIcon, styles.error)}>
                <HighlightOffIcon />
              </div>
              <Typography variant="title/modal-title">
                <FormattedMessage id="linkInstagram.modal.failed.title" />
              </Typography>
              <Typography variant="text/body small" className={styles.body}>
                <Markdown>{intl.formatMessage({ id: 'linkInstagram.modal.failed.message' })}</Markdown>
              </Typography>
            </div>
          </DialogContent>
          <DialogActions classes={{ root: styles.dialogActions }}>
            <Button classes={{ root: styles.button }} onClick={getAllPages}>
              <FormattedMessage id="global.cta.tryAgain" />
            </Button>
          </DialogActions>
        </>
      );
  }, [
    instaCoData,
    getAllPages,
    handleEmailChange,
    handleSubmitWithCustomEmail,
    handleSubmitWithSelectedInstagram,
    handleRadioChange,
    handleClose,
    intl,
    step,
    showTermsAndConditionsInsideModal,
    getFacebookKreators,
    checked,
    handleCheck,
    from,
  ]);

  const cnPaper = cn(styles.paper, {
    [styles.successModal]: from === 'kol-landing-page' && (step === 'newAccount' || step === 'accountExists'),
  });

  return (
    <div className={styles.wrapper}>
      <Helmet>
        <script
          id="facebook-jssdk"
          src="https://connect.facebook.net/fr_FR/sdk.js"
          crossOrigin="anonymous"
          async
          defer
        />
      </Helmet>
      <div className={styles.triggerButtons}>
        <Button
          classes={{
            root: styles.button,
            endIcon: styles.endIcon,
            disabled: styles.disabled,
          }}
          onClick={handleOpen}
          disabled={!checked && !showTermsAndConditionsInsideModal}
        >
          <FormattedMessage id="linkInstagram.button" />
        </Button>
        {showDownload && (
          <Button
            endIcon={<ArrowRightAltIcon />}
            classes={{
              root: styles.buttonPdf,
              endIcon: styles.endIcon,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <a
              target="_blank"
              rel="noopener noreferrer"
              download={`15-07-24_Insta_Connect_Onboarding_${locale === 'fr' ? 'FR' : 'EN'}.pdf`}
              href={`https://influence.kolsquare.com/pdf/15-07-24_Insta_Connect_Onboarding_${
                locale === 'fr' ? 'FR' : 'EN'
              }.pdf`}
            >
              <FormattedMessage id="linkInstagram.downloadPdf" />
            </a>
          </Button>
        )}
      </div>
      {!showTermsAndConditionsInsideModal && (
        <div className={styles.termsConditions}>
          <Checkbox color="default" checked={checked} onChange={handleCheck} />
          <Typography variant="label/large">
            <Markdown>{intl.formatMessage({ id: 'linkInstagram.termsConditions' })}</Markdown>
          </Typography>
        </div>
      )}

      <Dialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        maxWidth="xs"
        classes={{ root: styles.dialog, paper: cnPaper }}
      >
        <button className={styles.closeButton} onClick={handleClose} type="button" aria-label="close">
          <CloseIcon />
        </button>
        {loading && <Loader full background="rgba(255, 255, 255, .75)" />}

        {renderContent}
      </Dialog>
    </div>
  );
};

export default LinkInstagramButton;
